import ResizeSensor from 'resize-sensor';
import { FavouriteLayoutAttributes } from '../../types';
import { FavouriteRowValue, FavouriteDataModel } from './favourite-interfaces';
import { FavouriteModelComponent } from './favourite-model-component';
import { ChartLayoutConfig } from './layout-config';
import {
  createChartLayoutOptionsBuilder
} from '../chart-layout-options/chart-layout-options-builder-factory';
import {
  createChartDataOptionsBuilder
} from '../chart-data-options/chart-data-options-builder-factory';
import { FavouriteDataOptions } from './favourite-data-options';
import { ErrorHandler } from './error-handler';

import clone from 'clone';
import Highcharts from 'highcharts';
// Workaround for importing Highcharts using commonjs modules and typescript https://github.com/highcharts/highcharts/issues/4994
window.Highcharts = Highcharts;

import { ChartLayoutOptionsBuilder } from 'app/components/favourite-layout/favourite/chart-layout-options/chart-layout-options-builder';
import { ChartDataOptionsBuilder } from 'app/components/favourite-layout/favourite/chart-data-options/chart-data-options-builder';
import { DrillDownCallback, CaseViewCallback } from 'app/components/favourite-layout/favourite/common/drill-down-model';
import { debug } from 'app/infrastructure/log';

import merge from 'lodash/merge';
import debounce from 'lodash/debounce';

import heatmap from 'highcharts/modules/heatmap';
heatmap(Highcharts);

import highchartsExporting from 'highcharts/modules/exporting';
highchartsExporting(Highcharts);

(window as any).jsPDF = require('jspdf');
(window as any).svg2pdf = require('svg2pdf.js');

import highchartsOfflineExporting from 'highcharts/modules/offline-exporting';
import { DEBOUNCE_TIME_DEFAULT } from 'ui/constants';
highchartsOfflineExporting(Highcharts);

export class ChartFavouriteModelComponent extends FavouriteModelComponent {
  private chartLayoutOptionsBuilder: ChartLayoutOptionsBuilder;
  private sharedHighchartsOptions: Highcharts.Options;
  private chartElement: HTMLElement;
  private chart: Highcharts.Chart;
  private numberOfGroupByFields: number;

  constructor(layoutAttributes: FavouriteLayoutAttributes, intl: any, translation: any, errorHandler: ErrorHandler,
    drillDown: DrillDownCallback, caseview: CaseViewCallback, numberOfGroupByFields: number) {
    super(new ChartLayoutConfig(layoutAttributes), intl, translation, errorHandler, drillDown, caseview);

    this.chartLayoutOptionsBuilder = createChartLayoutOptionsBuilder(this.config as ChartLayoutConfig);
    this.sharedHighchartsOptions = this.createHighchartsOptions(intl);
    this.numberOfGroupByFields = numberOfGroupByFields;
  }

  private createHighchartsOptions(intl: any): Highcharts.Options {
    const options: Highcharts.Options = {
      title: { text: '' },
      chart: {
        style: {
          fontFamily: '"Nunito Sans", "Arial", "sans-serif"'
        }
      },
      series: [],
      credits: { enabled: false }
    };

    if (this.chartLayoutOptionsBuilder) {
      this.chartLayoutOptionsBuilder.setOptions(options);
    }

    // Override and merge any options that come from layout attributes.
    this.mergeStaticConfig(options);

    return options;
  }

  public setChartElement(element: HTMLElement) {

    this.chartElement = element;

    if (!this.chart) {
      this.createChart(element);
      // tslint:disable-next-line:no-unused-expression
      new ResizeSensor(element.parentElement, () => this.reflowChart());
    }
  }

  private createChart(element: HTMLElement) {
    Highcharts.chart(element, clone(this.sharedHighchartsOptions), (createdChart: Highcharts.Chart) => {
      this.chart = createdChart;
      this.chart.showLoading();
    });
  }

  public receiveData(dataModel: FavouriteDataModel) {
    super.receiveData(dataModel);

    if (this.chart) {
      this.chart.destroy();
    }

    this.addLoadingOnDrillDown();
    const dataOptions = new FavouriteDataOptions(this.config as ChartLayoutConfig, this.dataAdapter);
    this.dataAdapter.numberOfGroupByFields = this.numberOfGroupByFields;
    const chartDataOptionsBuilder =
      createChartDataOptionsBuilder(dataOptions, this.dataAdapter, this.drillDownHandler, this.config as ChartLayoutConfig);

    const splitRows = this.split(chartDataOptionsBuilder);
    const seriesValues = this.series(dataOptions, chartDataOptionsBuilder);

    for (let i = 0; i < splitRows.length; i++) {
      if (i > 0) {
        break; //TODO: Implement multichart split support.
      }

      const options = clone(this.sharedHighchartsOptions);
      chartDataOptionsBuilder.setOptions(options, splitRows[i], seriesValues);

      // Override and merge any options that come from layout attributes.
      this.mergeStaticConfig(options);
      Highcharts.chart(this.chartElement, options, (createdChart: Highcharts.Chart) => {
        this.chart = createdChart;
      });
    }

    this.chart.hideLoading();
  }

  public receiveLayout(layoutAttributes: FavouriteLayoutAttributes) {
    this.config = new ChartLayoutConfig(layoutAttributes);
    this.chartLayoutOptionsBuilder = createChartLayoutOptionsBuilder(this.config as ChartLayoutConfig);
    this.sharedHighchartsOptions = this.createHighchartsOptions(this.intl);
  }

  private addLoadingOnDrillDown() {
    const originalDrillDownFunction = this.drillDownHandler.toCaseListing.bind(this.drillDownHandler);
    this.drillDownHandler.toCaseListing = rowIndex => this.onDrillDownToCaseListing(rowIndex, originalDrillDownFunction);
  }

  private onDrillDownToCaseListing(rowIndex: number, drillDownFunction: any) {
    this.chart.showLoading();

    drillDownFunction(rowIndex);
  }

  public reflowChart: () => void = debounce(() => {
    if (this.chart) {
      this.chart.reflow();
    }
  }, DEBOUNCE_TIME_DEFAULT);

  private mergeStaticConfig(options: Highcharts.Options): Highcharts.Options {
    return merge(options, this.config.layoutAttributes.Highcharts);
  }

  private split(chartDataOptionsBuilder: ChartDataOptionsBuilder): FavouriteRowValue[] {
    return this.splitOrSeriesCategories('splitting', this.dataAdapter.dataModel.description, chartDataOptionsBuilder);
  }

  private series(dataOptions: FavouriteDataOptions, chartDataOptionsBuilder: ChartDataOptionsBuilder): FavouriteRowValue[] {
    return this.splitOrSeriesCategories(
      'series',
      dataOptions.getColumnTitle(chartDataOptionsBuilder.colIndexOf('x', 'mainSeries'), 'series'),
      chartDataOptionsBuilder
    );
  }

  private splitOrSeriesCategories(col: string, fallback: string, chartDataOptionsBuilder: ChartDataOptionsBuilder): FavouriteRowValue[] {
    const colIndex = chartDataOptionsBuilder.colIndexOf(col, 'mainSeries');
    if (colIndex >= 0) {
      return this.dataAdapter.columnCategories[colIndex];
    } else {
      return [{ value: fallback } as FavouriteRowValue];
    }
  }

  public destroy() {
    try {
      if (this.chart && this.chart.destroy) {
        this.chart.destroy();
      }
    } catch (e) {
      debug('Error destroying chart', e);
    }

    this.chart = null;

    if (this.chartElement) {
      ResizeSensor.detach(this.chartElement.parentElement);
    }
    this.chartElement = null;
  }
}
